import { useEffect, useState } from "react";
import AxiosInstance from "../../../shared/utils/axios";
import { Collapse, Divider, Flex } from "antd";
import { EditButton } from "../../../shared/layout/EditButton";

import { useAppState } from "../../../lib/appContext/AppContext";

import { useNewTrialState } from "../hooks/NewTrialStateProvider";
import { useTranslation } from "react-i18next";
import "../../style.less";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import type { NewTrialType } from "../types";
import useFormHandling from "../hooks/useFormHandling";
import { mergeStripPlotsIntoGroup } from "../hooks/splitStripPlots";
import SummaryRatesAndDosage from "./ratesAndDosage";
import { usePlotCardState } from "../../../shared/layout/PlotCard/utils";
import {
  isValidTrialId,
  ensureValidTrialId,
} from "../../../shared/utils/trialValidation";
import { formatAreaInHectares } from "../../../shared/utils/areaCalculation";
import "../../../config/styles/theme.less";

const emtpyTrail = {
  ab_line: [],
  buffer_zone: { type: "", size: 0 },
  machinery_grid: { width: 0 },
  trial_plots: [],
  name: "",
  protocol_name: "",
  start_date: "",
  end_date: "",
  standard_field_properties: {
    standard_fertilisers: [],
    standard_product: "",
    standard_seed_rate: 0,
  },
  archived: false,
  created_at: "",
  updated_at: "",
  deleted_at: "",
  protocol_id: "",
  assignee_id: "",
  assignee_name: "",
  field_ids: [],
  org_id: "",
  property_id: "",
  rates_and_dosages: null,
  id: "",
  is_exportable: false,
};

const SummaryComponent: React.FC = () => {
  // const id = "669965fb55034814ff4a3948";
  const { trialProtocolData, trialInfo, newTrialState } = useNewTrialState();
  const { fieldData, setCurrent } = useFormHandling();
  const { getAbLineColor } = usePlotCardState();
  const id = trialInfo.id;
  const { t } = useTranslation();
  const [summaryData, setSummaryData] = useState<NewTrialType>(emtpyTrail);
  const { stateValue } = useAppState();

  useEffect(() => {
    if (trialInfo) {
      setSummaryData((prevData) => ({
        ...prevData,
        ...trialInfo,
      }));
    }
  }, [trialInfo]);

  const assigneeData = stateValue?.assigneeValue;
  const getTrialData = async (): Promise<any> => {
    if (!isValidTrialId(id)) {
      console.warn("Cannot fetch trial data: trial ID is invalid", { id });
      return;
    }

    try {
      const validTrialId = ensureValidTrialId(
        id,
        "SummaryComponent.getTrialData"
      );
      const response = await AxiosInstance.get(`/trial/${validTrialId}`);
      // Strips are stored as N separate plots; the app treats them as one group.
      const loadedData = response.data;
      setSummaryData({
        ...loadedData,
        trial_plots: mergeStripPlotsIntoGroup(
          loadedData?.trial_plots,
          loadedData?.strip_design?.is_strip === true
        ),
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (isValidTrialId(id)) {
      getTrialData().catch((error) => {
        console.error("Error fetching data:", error);
      });
    }
  }, [id]);

  const currentAssignee = assigneeData?.find(
    (assigne: any) => assigne.value === trialInfo.assignee_id
  );

  const fieldName =
    fieldData?.length !== 0 &&
    fieldData?.find((field: any) => field.id === trialInfo?.field_ids[0])?.name;

  // Use current trial state for live updates, fallback to saved summary data
  const data: any = {
    ...summaryData,
    // Override with current state data to ensure we see live changes (like strips)
    trial_plots: newTrialState?.trial_plots || summaryData.trial_plots || [],
    ab_line: newTrialState?.ab_line || summaryData.ab_line || [],
    buffer_zone:
      Array.isArray(newTrialState?.buffer_zone) &&
      newTrialState.buffer_zone.length > 0
        ? newTrialState.buffer_zone[0]
        : summaryData.buffer_zone || { type: "", size: 0 },
    // Add strips data for detection
    stripsData: newTrialState?.stripsData,
  };

  // Helper function to detect if we're in strips mode
  const isStripsMode = Boolean(
    data.stripsData?.isStripMode ||
      data.trial_plots?.some((plot: any) => plot.plot?.isStripPlot)
  );

  const handleRedirection = async (step: number): Promise<void> => {
    if (!trialInfo?.id) {
      console.error("Cannot redirect: trialInfo.id is undefined or null.");
      return;
    }
    const response = await AxiosInstance.patch(
      `/trial/${trialInfo.id as string}`,
      {
        completion: false,
        completed_step: step,
      }
    );
    if (response.status === 200) {
      setCurrent(step);
    }
  };

  const isTrialCompleted = !!data?.completion;
  const completedDisabledTooltip = formatTranslation(
    t("trials.editDisabledCompleted")
  );

  return (
    <>
      <div className="summary-main">
        <h3 className="title">
          {formatTranslation(t("newTrial.summary.title"))}
        </h3>
        <p className="description">
          {formatTranslation(t("newTrial.summary.description"))}
        </p>

        <div className="summary-card-main">
          <div className="card-1">
            <Collapse
              ghost
              // icon can also be changed using the property below
              // expandIcon={(props)=><div></div>}
              expandIconPosition="end"
              collapsible="header"
              items={[
                {
                  label: (
                    <Flex justify="space-between">
                      <p className="text-xl text-semibold">
                        {formatTranslation(t("newTrial.summary.title1"))}
                      </p>
                      <EditButton
                        from="trialInfo"
                        onClick={() => {
                          handleRedirection(0).catch(console.error);
                        }}
                        disabled={isTrialCompleted}
                        disabledTooltip={completedDisabledTooltip}
                      />
                    </Flex>
                  ),
                  children: (
                    <div>
                      <div className="card-info-sec">
                        <div className="info">
                          <h5>
                            {formatTranslation(t("trials.tableTitle1"))}
                            <Divider className="text-md" type="vertical" />{" "}
                          </h5>
                          <p>{data.name}</p>
                        </div>
                        <div className="info">
                          <h5>
                            {formatTranslation(
                              t("newTrial.trialInfo.protocolSelection")
                            )}
                          </h5>
                          <p>{data.protocol_name}</p>
                        </div>
                        <div className="info">
                          <h5>
                            {formatTranslation(
                              t("newTrial.trialInfo.trialDate")
                            )}
                            <Divider className="text-md" type="vertical" />
                          </h5>
                          <p>
                            {data.start_date} to {data.end_date}
                          </p>
                        </div>
                        <div className="info">
                          <h5>{formatTranslation(t("trials.tableTitle3"))}</h5>
                          <p>{currentAssignee?.label}</p>
                        </div>
                        <div className="info">
                          <h5>
                            {formatTranslation(t("newTrial.summary.fields"))}
                            <Divider className="text-md" type="vertical" />
                          </h5>
                          <p>{fieldName}</p>
                        </div>
                      </div>
                    </div>
                  ),
                },
              ]}
            ></Collapse>
          </div>

          <div className="card-1">
            <Collapse
              ghost
              // icon can also be changed using the property below
              // expandIcon={(props)=><div></div>}
              expandIconPosition="end"
              collapsible="header"
              items={[
                {
                  label: (
                    <Flex justify="space-between">
                      <p className="text-xl text-semibold">
                        {isStripsMode
                          ? formatTranslation(
                              t("newTrial.summary.title2Stripes")
                            )
                          : formatTranslation(t("newTrial.summary.title2"))}
                      </p>
                      <EditButton
                        from="plotDesign"
                        onClick={() => {
                          handleRedirection(1).catch(console.error);
                        }}
                        disabled={isTrialCompleted}
                        disabledTooltip={completedDisabledTooltip}
                      />
                    </Flex>
                  ),
                  children: (
                    <div className="summary-plot-design">
                      {data.ab_line.length > 0 && (
                        <>
                          <Flex
                            gap={8}
                            vertical={true}
                            className="summary-ab-line"
                          >
                            <p className="text-lg text-semibold text-neutral-60 text-nomargin">
                              {formatTranslation(t("newTrial.summary.abLine"))}
                            </p>
                            {data.ab_line?.map((line: any, index: number) => (
                              <Flex key={index} gap={8} vertical={true}>
                                <Flex
                                  vertical={false}
                                  key={index}
                                  align="center"
                                >
                                  <Flex className="circle-container">
                                    <span
                                      className={`${
                                        getAbLineColor(
                                          line.id,
                                          "className"
                                        ) as string
                                      }  color-circle ab-blue`}
                                      // style={{backgroundColor: getAbLineColor(line.id,"hexCode")}}
                                    ></span>
                                  </Flex>
                                  <p className="text-md text-semibold text-nomargin">
                                    {line.name}
                                  </p>
                                </Flex>
                                <div className="text-md text-nomargin">
                                  <span className="text-semibold text-nomargin">
                                    {formatTranslation(
                                      t("newTrial.summary.machineryGridWidth")
                                    )}
                                  </span>{" "}
                                  <span className="text-sm">
                                    {line.grid_width} m
                                  </span>
                                </div>
                              </Flex>
                            ))}
                          </Flex>

                          {/* class text-md removes default margins from the divider */}
                          <Divider
                            className="text-md"
                            style={{ padding: "0", margin: "0px" }}
                          />
                        </>
                      )}
                      {data?.buffer_zone?.size && (
                        <Flex gap={12} vertical={true}>
                          <p className="text-lg text-semibold text-neutral-60 text-nomargin">
                            {formatTranslation(
                              t("newTrial.plotDesign.bufferZone")
                            )}
                          </p>
                          <p className="text-md text-semibold">
                            <span className=" text-semibold">
                              {data.buffer_zone?.type}:{" "}
                            </span>{" "}
                            <span className="text-sm">
                              {data.buffer_zone?.size} m
                            </span>
                          </p>

                          <Divider
                            className="text-md"
                            style={{ padding: "0", margin: "0px" }}
                          />
                        </Flex>
                      )}

                      <Flex vertical={true} gap={12}>
                        <p className="text-lg text-semibold text-neutral-60 text-nomargin">
                          {isStripsMode
                            ? "Strip"
                            : formatTranslation(
                                t("newTrial.plotDesign.trialPlots")
                              )}
                        </p>
                        {data.trial_plots?.map((plot: any, index: number) => {
                          if (plot.plot?.isStripPlot) {
                            // Get actual strip details from stripsData
                            const stripDetails =
                              data.stripsData?.stripDetails || [];
                            const stripCount =
                              stripDetails.length ||
                              plot.plot?.totalStrips ||
                              2;

                            return (
                              <div
                                key={index}
                                className="strips-summary-container"
                              >
                                {/* Strip details table */}
                                <div
                                  className="strips-summary-table"
                                  style={{
                                    marginTop: "8px",
                                    marginLeft: "16px",
                                  }}
                                >
                                  <table
                                    style={{
                                      borderCollapse: "collapse",
                                      width: "100%",
                                      fontSize: "12px",
                                    }}
                                  >
                                    <thead>
                                      <tr
                                        style={{ backgroundColor: "#f5f5f5" }}
                                      >
                                        <th
                                          style={{
                                            border: "1px solid #ddd",
                                            padding: "4px 8px",
                                            textAlign: "left",
                                          }}
                                        >
                                          Strip
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #ddd",
                                            padding: "4px 8px",
                                            textAlign: "left",
                                          }}
                                        >
                                          Length
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #ddd",
                                            padding: "4px 8px",
                                            textAlign: "left",
                                          }}
                                        >
                                          Width
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #ddd",
                                            padding: "4px 8px",
                                            textAlign: "left",
                                          }}
                                        >
                                          Area
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {stripDetails.length > 0
                                        ? stripDetails.map(
                                            (strip: any, i: number) => (
                                              <tr key={i}>
                                                <td
                                                  style={{
                                                    border: "1px solid #ddd",
                                                    padding: "4px 8px",
                                                  }}
                                                >
                                                  {i + 1}
                                                </td>
                                                <td
                                                  style={{
                                                    border: "1px solid #ddd",
                                                    padding: "4px 8px",
                                                  }}
                                                >
                                                  {(
                                                    strip.stripLength || 0
                                                  ).toFixed(1)}{" "}
                                                  m
                                                </td>
                                                <td
                                                  style={{
                                                    border: "1px solid #ddd",
                                                    padding: "4px 8px",
                                                  }}
                                                >
                                                  {(
                                                    strip.stripWidth || 0
                                                  ).toFixed(1)}{" "}
                                                  m
                                                </td>
                                                <td
                                                  style={{
                                                    border: "1px solid #ddd",
                                                    padding: "4px 8px",
                                                  }}
                                                >
                                                  {formatAreaInHectares(
                                                    strip.area
                                                  )}
                                                </td>
                                              </tr>
                                            )
                                          )
                                        : Array.from(
                                            { length: stripCount },
                                            (_, i) => (
                                              <tr key={i}>
                                                <td
                                                  style={{
                                                    border: "1px solid #ddd",
                                                    padding: "4px 8px",
                                                  }}
                                                >
                                                  {i + 1}
                                                </td>
                                                <td
                                                  style={{
                                                    border: "1px solid #ddd",
                                                    padding: "4px 8px",
                                                  }}
                                                >
                                                  Calculating...
                                                </td>
                                                <td
                                                  style={{
                                                    border: "1px solid #ddd",
                                                    padding: "4px 8px",
                                                  }}
                                                >
                                                  Calculating...
                                                </td>
                                                <td
                                                  style={{
                                                    border: "1px solid #ddd",
                                                    padding: "4px 8px",
                                                  }}
                                                >
                                                  Calculating...
                                                </td>
                                              </tr>
                                            )
                                          )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          } else {
                            // Show regular plot information
                            return (
                              <p className="text-md" key={index}>
                                <span className="text-semibold">
                                  {formatTranslation(
                                    t(
                                      "trials.plotDesign.trialPlots.table.header1"
                                    )
                                  )}{" "}
                                  {String.fromCharCode(65 + index)}
                                </span>{" "}
                                <span>
                                  {plot.plot.rows * plot.plot.columns} plots{" "}
                                  {plot.plot.rows} x {plot.plot.columns} |{" "}
                                  {plot.plot.row_size}m x{" "}
                                  {plot.plot.column_size}m{" "}
                                </span>
                              </p>
                            );
                          }
                        })}
                      </Flex>
                    </div>
                  ),
                },
              ]}
            ></Collapse>
          </div>

          <div className="card-1">
            <Collapse
              defaultActiveKey={"active"}
              ghost
              key={"active"}
              expandIconPosition="end"
              collapsible="header"
              items={[
                {
                  label: (
                    <Flex justify="space-between">
                      <p className="text-xl text-semibold">
                        {formatTranslation(t("newTrial.summary.title3"))}
                      </p>
                      <EditButton
                        from="ratesAndDosage"
                        onClick={() => {
                          handleRedirection(2).catch(console.error);
                        }}
                        disabled={isTrialCompleted}
                        disabledTooltip={completedDisabledTooltip}
                      />
                    </Flex>
                  ),
                  key: "active",
                  children: (
                    <>
                      <SummaryRatesAndDosage
                        data={data}
                        trialProtocolData={trialProtocolData}
                      ></SummaryRatesAndDosage>
                    </>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SummaryComponent;
